import React, { useEffect } from 'react';
import { Routes, Route, Outlet, Navigate } from 'react-router-dom';
import './App.css';
import Landing from './pages/Landing';
import EmployerLogin from './pages/EmployerLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import { isLogged, isLoggedUnsecuredToken, logout, isAdminLogged, getUser, checkTokenExpiration } from './services/helpers';
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import AdminHospitals from './admin/pages/AdminHospitals';
import AdminPatients from './admin/pages/AdminPatients';
import AdminRequests from './admin/pages/AdminRequests';
import AdminSystemUsers from './admin/pages/AdminSystemUsers';
import AdminNewUser from './admin/pages/AdminNewUser';
import AdminUserDetails from './admin/pages/AdminUserDetails';
import AdminPayments from './admin/pages/AdminPayments';
import AdminPaymentDetails from './admin/pages/AdminPaymentDetails';
import AdminPayouts from './admin/pages/AdminPayouts';
import AdminPayoutDetails from './admin/pages/AdminPayoutDetails';
import AdminDisputes from './admin/pages/AdminDisputes';
import AdminDisputeDetails from './admin/pages/AdminDisputeDetails';
import AdminReports from './admin/pages/AdminReports';
import AdminAudit from './admin/pages/AdminAudit';
import Profile from './admin/pages/Profile';
import AdminHospitalDetails from './admin/pages/AdminHospitalDetails';
import AdminPatientDetails from './admin/pages/AdminPatientDetails';
import AdminRequestDetails from './admin/pages/AdminRequestDetails';
import EmployerSignup from './pages/EmployerSignup';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import ContactUs from './pages/ContactUs';
import BlogPost from './pages/BlogPost';
import EmploymeAdminDashboard from './admin/EmploymeAdminDashboard';
import AdminJobApprovals from './admin/pages/AdminJobApprovals';
import AdminJobApprovalDetails from './admin/pages/AdminJobApprovalDetails';
import AdminJobPosts from './admin/pages/AdminJobPosts';
import AdminEmployers from './admin/pages/AdminEmployers';
import AdminEmployerDetails from './admin/pages/AdminEmployerDetails';
import AdminJobSeekers from './admin/pages/AdminJobSeekers';
import AdminJobSeekerDetails from './admin/pages/AdminJobSeekerDetails';
import AdminCompanies from './admin/pages/AdminCompanies';
import AdminCompanyDetails from './admin/pages/AdminCompanyDetails';
import AdminTemplates from './admin/pages/AdminTemplates';
import AdminEmploymePayments from './admin/pages/AdminEmploymePayments';
import AdminEmploymePaymentDetails from './admin/pages/AdminEmploymePaymentDetails';
import AdminReminders from './admin/pages/AdminReminders';
import AdminEmploymeSettings from './admin/pages/AdminEmploymeSettings';
import MaintenanceGate from './pages/MaintenanceGate';
import JobSeekerLayout from './employme/JobSeekerLayout';
import JobListPage from './employme/JobListPage';
import JobDetailsPage from './employme/JobDetailsPage';
import JobApplicationsPage from './employme/JobApplicationsPage';
import JobSeekerVerifyEmail from './employme/JobSeekerVerifyEmail';
import EmployerVerifyEmail from './employer/EmployerVerifyEmail';
import EmployerDashboardLayout from './employer/EmployerDashboardLayout';
import EmployerDashboard from './employer/EmployerDashboard';
import EmployerJobsList from './employer/EmployerJobsList';
import EmployerNewJob from './employer/EmployerNewJob';
import EmployerJobDetails from './employer/EmployerJobDetails';
import EmployerJobRankingPage from './employer/EmployerJobRankingPage';
import EmployerApplicantDetails from './employer/EmployerApplicantDetails';
import EmployerApplicantsList from './employer/EmployerApplicantsList';
import EmployerMessagesLayout from './employer/EmployerMessagesLayout';
import EmployerIndividualMessages from './employer/EmployerIndividualMessages';
import EmployerGeneralMessages from './employer/EmployerGeneralMessages';
import EmployerProfile from './employer/EmployerProfile';
import EmployerOnboarding from './employer/EmployerOnboarding';
import CvScreeningListPage from './employer/cv-screening/CvScreeningListPage';
import CvScreeningWizardPage from './employer/cv-screening/CvScreeningWizardPage';
import CvScreeningBatchPage from './employer/cv-screening/CvScreeningBatchPage';
import CvScreeningItemPage from './employer/cv-screening/CvScreeningItemPage';
import JobSeekerDashboardPage from './employme/JobSeekerDashboardPage';
import PublicCareersLayout from './employme/PublicCareersLayout';
import JobSeekerOnboarding from './employme/JobSeekerOnboarding';
import JobSeekerProfilePage from './employme/JobSeekerProfilePage';
import JobSeekerProfileAiAssist from './employme/JobSeekerProfileAiAssist';
import JobInterviewPage from './employme/JobInterviewPage';
import { captureReferralFromUrl } from './services/referralAttribution';
import { useLocation } from 'react-router-dom';

const JobSeekerHome = () => {
  const isLoggedIn =
    typeof window !== 'undefined' &&
    (sessionStorage.getItem('employme-active') === '1' || !!localStorage.getItem('employmeUser'))
  if (!isLoggedIn) {
    return <Navigate to="/jobs/browse" replace />
  }
  return <JobSeekerDashboardPage />
}

function App() {
  const location = useLocation()

  useEffect(() => {
    captureReferralFromUrl()
  }, [location.search])

  // Check token expiration periodically (every 5 minutes)
  useEffect(() => {
    const checkExpiration = () => {
      checkTokenExpiration()
    }
    
    // Check immediately
    checkExpiration()
    
    // Check every 5 minutes
    const interval = setInterval(checkExpiration, 5 * 60 * 1000)
    
    return () => clearInterval(interval)
  }, [])
  
  window.onclose = (e) => {
    logout()
  }
  
  const LoggedRoute = () => {
    const logged = isLogged()
    if(logged){
      const user = getUser()
      if(user?.role === 'EMPLOYER'){
        return <Navigate to={'/employer/dashboard'} replace />
      }
      if(user?.role === 'JOB_SEEKER'){
        return <Navigate to={'/jobs'} replace />
      }
    }
    return <Outlet/>
  }

  const AdminPrivateRoute = () => {
    const logged = isAdminLogged()
    if(logged){
      return <Outlet/>
    }else{
      return <Navigate to={'/admin'} replace />
    }
  }
  return (
      <Routes>
        <Route exact path="admin" element={<AdminLogin/>} />
        <Route element={<AdminPrivateRoute/>}>
          <Route path="admin/app" element={<AdminLayout/>}>
            <Route index element={<Navigate to="employme" replace />} />
            <Route path="employme" element={<EmploymeAdminDashboard />} />
            <Route path="employme/jobs" element={<AdminJobPosts />} />
            <Route path="employme/job-approvals" element={<AdminJobApprovals />} />
            <Route path="employme/job-approvals/:jobId" element={<AdminJobApprovalDetails />} />
            <Route path="employme/employers" element={<AdminEmployers />} />
            <Route path="employme/employers/:userId" element={<AdminEmployerDetails />} />
            <Route path="employme/job-seekers" element={<AdminJobSeekers />} />
            <Route path="employme/job-seekers/:userId" element={<AdminJobSeekerDetails />} />
            <Route path="employme/companies" element={<AdminCompanies />} />
            <Route path="employme/companies/:companyId" element={<AdminCompanyDetails />} />
            <Route path="employme/templates" element={<AdminTemplates />} />
            <Route path="employme/payments" element={<AdminEmploymePayments />} />
            <Route path="employme/payments/:jobId" element={<AdminEmploymePaymentDetails />} />
            <Route path="employme/settings" element={<AdminEmploymeSettings />} />
            <Route path="employme/reminders" element={<AdminReminders />} />
            <Route path="hospitals" element={<AdminHospitals/>} />
            <Route path="hospitals/:userId" element={<AdminHospitalDetails/>} />
            <Route path="patients" element={<AdminPatients/>} />
            <Route path="patients/:userId" element={<AdminPatientDetails/>} />
        <Route path="requests" element={<AdminRequests/>} />
        <Route path="requests/:postId" element={<AdminRequestDetails/>} />
        <Route path="payments" element={<AdminPayments/>} />
        <Route path="payments/:paymentId" element={<AdminPaymentDetails/>} />
        <Route path="payouts" element={<AdminPayouts/>} />
        <Route path="payouts/:payoutId" element={<AdminPayoutDetails/>} />
        <Route path="disputes" element={<AdminDisputes/>} />
        <Route path="disputes/:disputeId" element={<AdminDisputeDetails/>} />
        <Route path="reports" element={<AdminReports/>} />
        <Route path="system-users" element={<AdminSystemUsers/>} />
            <Route path="system-users/new" element={<AdminNewUser/>} />
            <Route path="system-users/:adminId" element={<AdminUserDetails/>} />
            <Route path="audit" element={<AdminAudit/>} />
            <Route path="profile" element={<Profile/>} />
          </Route>
        </Route>
        <Route element={<MaintenanceGate />}>
          <Route exact path="/" element={<Landing/>} />
          <Route exact path="privacy-policy" element={<PrivacyPolicy/>} />
          <Route exact path="terms-of-service" element={<TermsOfService/>} />
          <Route exact path="cookie-policy" element={<CookiePolicy/>} />
          <Route exact path="contact" element={<ContactUs/>} />
          <Route exact path="blog/:slug" element={<BlogPost/>} />
          <Route path="careers" element={<PublicCareersLayout />}>
            <Route index element={<JobListPage />} />
            <Route path=":jobId" element={<JobDetailsPage />} />
          </Route>
          <Route element={<LoggedRoute/>}>
            <Route exact path="login" element={<EmployerLogin/>} />
            <Route exact path="signup" element={<EmployerSignup/>} />
            <Route exact path="forgot-password" element={<ForgotPassword/>} />
            <Route exact path="reset-password/:userId/:token" element={<ResetPassword/>} />
            <Route exact path="verify-email/:userId/:token" element={<VerifyEmail/>} />
            <Route exact path="employer/verify-email/:userId/:token" element={<EmployerVerifyEmail />} />
            <Route exact path="signup/employer" element={<Navigate to="/signup?role=employer" replace />} />
            <Route exact path="payment/success" element={<PaymentSuccess/>} />
            <Route exact path="payment/failed" element={<PaymentFailed/>} />
          </Route>
          <Route path="jobs/login" element={<Navigate to="/login" replace />} />
          <Route path="jobs/signup" element={<Navigate to="/signup" replace />} />
          <Route path="jobs/forgot-password" element={<Navigate to="/forgot-password" replace />} />
          <Route path="jobs" element={<JobSeekerLayout />}>
            <Route index element={<JobSeekerHome />} />
            <Route path="browse" element={<JobListPage />} />
            <Route path="applications" element={<JobApplicationsPage />} />
            <Route path="applications/:applicationId/interview" element={<JobInterviewPage />} />
            <Route path="onboarding" element={<JobSeekerOnboarding />} />
            <Route path="profile" element={<JobSeekerProfilePage />} />
            <Route path="profile/ai-assist" element={<JobSeekerProfileAiAssist />} />
            <Route path="verify-email/:userId/:token" element={<JobSeekerVerifyEmail />} />
            <Route path=":jobId" element={<JobDetailsPage />} />
          </Route>
          <Route path="employer/onboarding" element={<EmployerOnboarding />} />
          <Route path="employer" element={<EmployerDashboardLayout/>}>
            <Route path="dashboard" element={<EmployerDashboard/>} />
            <Route path="jobs" element={<EmployerJobsList/>} />
            <Route path="jobs/new" element={<EmployerNewJob/>} />
            <Route path="jobs/:jobId/edit" element={<EmployerNewJob/>} />
            <Route path="jobs/:jobId" element={<EmployerJobDetails/>} />
            <Route path="jobs/:jobId/ranking" element={<EmployerJobRankingPage/>} />
            <Route path="jobs/:jobId/applicants/:applicantId" element={<EmployerApplicantDetails/>} />
            <Route path="applicants" element={<EmployerApplicantsList/>} />
            <Route path="cv-screening" element={<CvScreeningListPage />} />
            <Route path="cv-screening/new" element={<CvScreeningWizardPage />} />
            <Route path="cv-screening/:batchId" element={<CvScreeningBatchPage />} />
            <Route path="cv-screening/:batchId/items/:itemId" element={<CvScreeningItemPage />} />
            <Route path="messages" element={<EmployerMessagesLayout />}>
              <Route index element={<Navigate to="individual" replace />} />
              <Route path="individual" element={<EmployerIndividualMessages />} />
              <Route path="general" element={<EmployerGeneralMessages />} />
            </Route>
            <Route path="profile" element={<EmployerProfile/>} />
          </Route>
        </Route>
      </Routes>
  );
}

export default App;
